import type { SessionState, ModeratorState, TranscriptSource } from "../../shared/types.ts";
import { readStoredAuth, type AuthRole, type RoleAuth } from "./auth.ts";

export interface HealthResponse {
  ok: boolean;
  provider: string;
  runtimeProvider: string;
  sessionProvider: string | null;
  sessionId: string | null;
  transcriptSource: TranscriptSource;
  status: SessionState["status"] | null;
  replay: SessionState["replay"] | null;
  runtime: {
    nodeEnv: string;
    requireAuthTokens: boolean;
    convexUrlHost: string;
    viteConvexUrlHost: string;
    sttMode: "disabled" | "mock" | "live";
    sttProvider: "deepgram" | "speechmatics" | "soniox";
    sttProviderKeyConfigured: boolean;
    sttProviderKeys: {
      deepgram: boolean;
      speechmatics: boolean;
      soniox: boolean;
    };
    ingressSharedSecretConfigured: boolean;
    operatorAuthConfigured: boolean;
    moderatorAuthConfigured: boolean;
  };
  readiness: {
    manualFallbackReady: true;
    authReady: boolean;
    authEnforced: boolean;
    liveSttReady: boolean;
    sttReady: boolean;
    groqRequested: boolean;
    sameOriginAudioIngress: boolean;
    readyForLiveEvent: boolean;
    warnings: string[];
  };
  audioIngress: {
    path: string;
    sttMode: "disabled" | "mock" | "live";
  };
}

export interface ManualSuggestionGenerationResponse {
  created: string[];
  provider: string;
  reason?: string;
  candidateCount: number;
  keptCount: number;
}

async function post(path: string, body?: unknown, authOverride?: RoleAuth): Promise<unknown> {
  const res = await fetch(`/api${path}`, {
    method: "POST",
    headers: { "content-type": "application/json", ...authHeaders(authOverride) },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) throw new Error(`${path} -> ${res.status}`);
  return res.json().catch(() => ({}));
}

async function get(path: string): Promise<unknown> {
  const res = await fetch(`/api${path}`, { headers: authHeaders() });
  if (!res.ok) throw new Error(`${path} -> ${res.status}`);
  return res.json();
}

function authHeaders(authOverride?: RoleAuth): Record<string, string> {
  const auth = authOverride ?? readStoredAuth("operator") ?? readStoredAuth("moderator");
  if (!auth) return {};
  return {
    "x-comodo-role": auth.role,
    "x-comodo-token": auth.token,
  };
}

export const api = {
  checkAccess: (auth: RoleAuth) => post("/access-check", undefined, auth),
  getDevAuth: (role: AuthRole) => get(`/dev-auth?role=${encodeURIComponent(role)}`) as Promise<RoleAuth>,
  // transcript
  addTranscript: (entry: { speaker: string; speakerKey?: string | null; text: string; source: TranscriptSource; final?: boolean; ts?: number }) =>
    post("/transcript", entry),
  // replay
  startReplay: (speed = 1) => post("/replay/start", { speed }),
  pauseReplay: () => post("/replay/pause"),
  resumeReplay: () => post("/replay/resume"),
  resetReplay: () => post("/replay/reset"),
  // generation (suggestion lifecycle now flows through Convex audience mutations)
  generateNow: (input: {
    force?: boolean;
    kind?: "suggestions" | "combined";
    provider?: "groq" | "mock";
  } = {}) => post("/generate", input) as Promise<ManualSuggestionGenerationResponse>,
  // notes + mic + session
  setNotes: (notes: string) => post("/notes", { notes }),
  setSttStatus: (level: number) => post("/stt-status", { level }),
  getHealth: () => get("/health") as Promise<HealthResponse>,
  resetSession: () => post("/session/reset"),
};

export function getAudioIngressUrl(): string {
  const configured = import.meta.env.VITE_AUDIO_INGRESS_URL as string | undefined;
  if (configured?.trim()) return configured.trim();
  const protocol = window.location.protocol === "https:" ? "wss" : "ws";
  return `${protocol}://${window.location.host}/audio-ingress`;
}

export type Role = "operator" | "moderator";

function wsUrl(role: Role): string {
  const proto = window.location.protocol === "https:" ? "wss" : "ws";
  return `${proto}://${window.location.host}/ws?role=${role}`;
}

/** Connect to the backend WS and invoke onState/onModerator on each push. */
export function connectWS(
  role: Role,
  handlers: { onState?: (s: SessionState) => void; onModerator?: (m: ModeratorState) => void; onStatus?: (connected: boolean) => void },
): () => void {
  let ws: WebSocket | null = null;
  let closed = false;
  let retry: ReturnType<typeof setTimeout> | null = null;

  const open = () => {
    if (closed) return;
    ws = new WebSocket(wsUrl(role));
    ws.onopen = () => handlers.onStatus?.(true);
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data as string);
        if (msg.type === "state") handlers.onState?.(msg.payload);
        else if (msg.type === "moderator") handlers.onModerator?.(msg.payload);
      } catch {
        /* ignore malformed frames */
      }
    };
    ws.onclose = () => {
      handlers.onStatus?.(false);
      if (!closed) retry = setTimeout(open, 1000);
    };
    ws.onerror = () => ws?.close();
  };
  open();

  return () => {
    closed = true;
    if (retry) clearTimeout(retry);
    ws?.close();
  };
}
